import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { BeatLoader, ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";

import sidebarIcon from "../../assets/sidebar_icon.svg";
import searchIcon from "../../assets/search_icon.svg";
import editIcon from "../../assets/edit_icon.svg";
import regenerateIcon from "../../assets/regenerate_icon.svg";
import sendIcon from "../../assets/send_icon.svg";

import NewChat from "./NewChat";
import { useGetChatMessages, useSendQuery, useRegenerateResponse } from "./api";
import { openSidebar } from "../../redux/slices/sidebarSlice";
import { openSearchModal } from "../../redux/slices/searchModalSlice";
import { RootState } from "../../redux/store";
import { ChatMessage } from "../../types/type";

type QueryInput = { content: string };
type EditInput = { content: string };

interface DetailItem {
  text: string;
  source: string;
}

interface MitigationItem {
  text: string;
  source: string;
}

interface AIResponseContent {
  text?: string;
  summary?: string;
  details?: DetailItem[];
  mitigations?: MitigationItem[];
}

export default function Chat() {
  const dispatch = useDispatch();
  const { isSidebarOpen } = useSelector((state: RootState) => state.sidebar);
  const { isModalOpen } = useSelector((state: RootState) => state.searchModal);
  const [serverError, setServerError] = useState("");
  const [isMessageEditing, setIsMessageEditing] = useState<number>();

  const { sessionId } = useParams<{ sessionId: string }>();
  const sessionIdNum = sessionId ? parseInt(sessionId, 10) : null;

  const sendQueryMutation = useSendQuery();
  const regenerateResponseMutation = useRegenerateResponse();

  // form for new messages
  const {
    handleSubmit: handleQuerySubmit,
    register: registerQuery,
    watch: watchQuery,
    reset: resetQuery,
  } = useForm<QueryInput>();

  // form for editing messages
  const {
    handleSubmit: handleEditSubmit,
    register: registerEdit,
    reset: resetEdit,
  } = useForm<EditInput>();

  const watchedContent = watchQuery("content", "");

  const {
    data: messages = [],
    isLoading,
    error,
  } = useGetChatMessages(sessionIdNum as number);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) setTimeout(scrollToBottom, 100);
  }, [messages]);

  useEffect(() => {
    if (sendQueryMutation.isPending) setTimeout(scrollToBottom, 100);
  }, [sendQueryMutation.isPending]);

  useEffect(() => {
    if (serverError) toast.error(serverError);
  }, [serverError]);

  // Toast any technical error from initial messages load
  useEffect(() => {
    if (error) {
      const anyErr: any = error as any;
      const message = anyErr?.response?.data?.message || "Application Error. Please contact support.";
      setServerError(message);
    }
  }, [error]);

  // Frontend validation function
  const validateQuery = (query: string) => {
    const errors: string[] = [];
    
    if (!query.trim()) {
      errors.push("Please enter a message");
    }
    
    if (query.length > 4000) {
      errors.push("Message too long (max 4000 characters)");
    }
    
    // Check for potentially harmful patterns
    const harmfulPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /data:text\/html/gi
    ];
    
    if (harmfulPatterns.some(pattern => pattern.test(query))) {
      errors.push("Message contains invalid content");
    }
    
    return errors;
  };

  const onSubmit = (data: QueryInput) => {
    if (!sessionIdNum) {
      setServerError("Session ID is not valid");
      return;
    }
    if (!navigator.onLine) {
      setServerError("No internet connection. Please check your network.");
      return;
    }
    
    // Frontend validation
    const validationErrors = validateQuery(data.content);
    if (validationErrors.length > 0) {
      setServerError(validationErrors[0]);
      return;
    }

    sendQueryMutation.mutate(
      { sessionId: sessionIdNum, query: data.content },
      {
        onSuccess: () => {
          resetQuery();
          setServerError("");
          setTimeout(scrollToBottom, 200);
        },
        onError: (err: any) => {
          setServerError(
            err.response?.data?.message ||
              "An error occurred while sending your message"
          );
        },
      }
    );
  };

  const handleRegenerateResponse = (messageId: number) => {
    if (!sessionIdNum) {
      setServerError("Session ID is not valid");
      return;
    }
    if (!navigator.onLine) {
      setServerError("No internet connection. Please check your network.");
      return;
    }

    regenerateResponseMutation.mutate(
      { sessionId: sessionIdNum, messageId },
      {
        onSuccess: () => {
          setTimeout(scrollToBottom, 200);
        },
        onError: (err: any) => {
          setServerError(
            err.response?.data?.message ||
              "An error occurred while regenerating the response"
          );
        },
      }
    );
  };

  const parseAIResponse = (message: ChatMessage): AIResponseContent => {
    if (typeof message.content === "object" && message.content !== null) {
      return message.content as AIResponseContent;
    }
    if (typeof message.content === "string") {
      try {
        return JSON.parse(message.content);
      } catch {
        return { text: message.content };
      }
    }
    return { text: "" };
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleQuerySubmit(onSubmit)();
    }
  };

  if (isLoading)
    return (
      <div
        className={`bg-white h-[calc(100vh-5rem)] items-center justify-center flex ${
          isSidebarOpen ? "w-3/4" : "w-full"
        }`}
      >
        <ClipLoader
          color='#0F4988'
          loading={isLoading}
          size={window.innerWidth < 640 ? 70 : 150}
          aria-label='Loading Spinner'
          data-testid='loader'
        />
      </div>
    );

  return (
    <div
      className={`bg-white h-[calc(100vh-5rem)] px-8 flex ${
        isSidebarOpen ? "w-full md:3/4" : "w-full"
      }`}
    >
      {!isSidebarOpen && (
        <div className='flex justify-center items-start w-1/7 -ml-4 mt-4 mr-2'>
          <div>
            <img
              src={sidebarIcon}
              onClick={() => dispatch(openSidebar())}
              alt='Open Sidebar'
              className='w-8 h-8 cursor-pointer mr-3 md:w-10 md:h-10'
              data-tooltip-id='open-sidebar-tooltip'
            />
            <Tooltip
              id='open-sidebar-tooltip'
              place='top'
              content='Open Sidebar'
            />
          </div>
          <div>
            <img
              src={searchIcon}
              onClick={() => dispatch(openSearchModal())}
              alt='Search Chats'
              className='w-8 h-8 cursor-pointer md:w-10 md:h-10'
              data-tooltip-id='search-chats-tooltip'
            />
            <Tooltip
              id='search-chats-tooltip'
              place='bottom'
              content='Search Chats'
            />
          </div>
        </div>
      )}

      {sessionIdNum !== null ? (
        <div className='flex flex-col flex-1 relative text-black'>
          <div
            ref={messagesContainerRef}
            className='flex-1 overflow-y-auto scroll-smooth pb-24'
            style={{ scrollBehavior: "smooth" }}
          >
            {messages.map((message: ChatMessage) => (
              <div key={message.message_id}>
                {message.message_role === "USER" ? (
                  <div className='flex justify-end items-end mt-10 w-full'>
                    {isMessageEditing === message.message_id ? (
                      <form
                        className='w-3/4 flex flex-col gap-2'
                        onSubmit={handleEditSubmit((data) => {
                          onSubmit(data);
                          setIsMessageEditing(undefined);
                          resetEdit();
                        })}
                      >
                        <textarea
                          className='w-full text-black border-2 border-[#D9D9D9] rounded px-3 py-2 resize-none min-h-[40px] max-h-[120px]'
                          {...registerEdit("content", {
                            required: "Please enter a message",
                            onChange: (e) => {
                              e.target.style.height = "auto";
                              e.target.style.height =
                                Math.min(e.target.scrollHeight, 120) + "px";
                            },
                          })}
                        />
                        <div className='flex justify-end gap-2'>
                          <button
                            type='button'
                            className='bg-gray-300 text-black px-4 py-2 rounded'
                            onClick={() => {
                              setIsMessageEditing(undefined);
                              resetEdit();
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type='submit'
                            className='bg-blue-500 text-white px-4 py-2 rounded'
                          >
                            Send
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className='flex items-end w-full justify-end'>
                        <button
                          onClick={() => {
                            resetEdit({
                              content: message.content as string,
                            });
                            setIsMessageEditing(message.message_id);
                          }}
                          className='mr-4'
                          data-tooltip-id='edit-tooltip'
                        >
                          <img
                            src={editIcon}
                            alt='Edit'
                            className='w-6 h-6 cursor-pointer'
                          />
                        </button>
                        <Tooltip id='edit-tooltip' place='top' content='Edit' />
                        <div className='bg-[#F5F5F5] rounded-lg p-4 w-3/4'>
                          <p className='whitespace-pre-wrap'>
                            {message.content}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='flex justify-start items-end mt-10 w-full'>
                    <div className='bg-[#E0E0E0] w-3/4 rounded-lg p-4'>
                      {(() => {
                        const content = parseAIResponse(message);
                        return (
                          <>
                            {content.summary && (
                              <div className='mb-4'>
                                <h3 className='font-bold text-lg border-b border-gray-400 pb-1 mb-2'>
                                  Summary
                                </h3>
                                <p className='whitespace-pre-wrap'>
                                  {content.summary}
                                </p>
                              </div>
                            )}
                            {content.details && content.details.length > 0 && (
                              <div className='mb-4'>
                                <h3 className='font-bold text-lg border-b border-gray-400 pb-1 mb-2'>
                                  Details
                                </h3>
                                <ul className='list-disc pl-5 space-y-1'>
                                  {content.details.map(
                                    (detail: DetailItem, idx: number) => (
                                      <li key={idx} className='mb-1'>
                                        <span>{detail.text}</span>
                                        <span className='text-xs text-gray-500 block'>
                                          {detail.source}
                                        </span>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                            {content.mitigations &&
                              content.mitigations.length > 0 && (
                                <div>
                                  <h3 className='font-bold text-lg border-b border-gray-400 pb-1 mb-2'>
                                    Mitigations
                                  </h3>
                                  <ul className='list-disc pl-5 space-y-1'>
                                    {content.mitigations.map(
                                      (mit: MitigationItem, idx: number) => (
                                        <li key={idx} className='mb-1'>
                                          <span>{mit.text}</span>
                                          <span className='text-xs text-gray-500 block'>
                                            {mit.source}
                                          </span>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}
                            {!content.summary &&
                              !content.details &&
                              !content.mitigations && (
                                <p className='whitespace-pre-wrap'>
                                  {content.text}
                                </p>
                              )}
                          </>
                        );
                      })()}
                    </div>
                    <button
                      onClick={() =>
                        handleRegenerateResponse(message.message_id)
                      }
                      disabled={regenerateResponseMutation.isPending}
                      className='disabled:opacity-50 disabled:cursor-not-allowed ml-4'
                      data-tooltip-id='regenerate-tooltip'
                    >
                      <img
                        src={regenerateIcon}
                        alt='Regenerate'
                        className='w-5 h-5 cursor-pointer'
                      />
                    </button>
                    <Tooltip
                      id='regenerate-tooltip'
                      place='top'
                      content='Regenerate'
                    />
                  </div>
                )}
              </div>
            ))}
            {(sendQueryMutation.isPending ||
              regenerateResponseMutation.isPending) && (
              <div className='flex justify-start items-center mt-10'>
                <div className='bg-[#9E9E9E] rounded-lg p-4'>
                  <BeatLoader
                    color='#ffffff'
                    loading={true}
                    size={8}
                    aria-label='AI is thinking...'
                  />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form
            className='absolute bottom-0 left-0 right-0 flex items-center gap-2 p-4 bg-white shadow-t'
            onSubmit={handleQuerySubmit(onSubmit)}
          >
            <div className='flex-1 relative'>
              <textarea
                className='w-full text-black border-2 border-[#D9D9D9] rounded-md px-3 py-2 resize-none min-h-[55px] max-h-[120px] overflow-y-auto outline-none'
                placeholder='Ask your question here...'
                rows={1}
                maxLength={350000}
                disabled={sendQueryMutation.isPending}
                {...registerQuery("content", {
                  required: "Please enter a message",
                  onChange: (e) => {
                    e.target.style.height = "auto";
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 120) + "px";
                  },
                })}
                onKeyDown={handleKeyDown}
              />
              <span className='absolute bottom-2 right-2 text-xs text-gray-500 select-none'>
                {watchedContent.length.toLocaleString()} /{" "}
                {(350000).toLocaleString()}
              </span>
            </div>
            <button
              type='submit'
              disabled={sendQueryMutation.isPending || !watchedContent.trim()}
              className='flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {sendQueryMutation.isPending ? (
                <BeatLoader color='#0F4988' loading size={10} />
              ) : (
                <img
                  src={sendIcon}
                  alt='Send Icon'
                  className='w-9 h-9 cursor-pointer'
                />
              )}
            </button>
          </form>
        </div>
      ) : (
        <NewChat />
      )}
    </div>
  );
}
